import { css } from '@emotion/react';
import { useTenantDetailsQuery } from '@frontend/api-tenants';
import { useLocationServiceContext } from '@frontend/wam-location-service';
import { theme } from '@frontend/theme';
import { Button, Heading, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';
import { Dot } from '../../components/dot';
import { OutlinedCard, SimpleInfoOutlinedCard } from '../../components/outlined-card';
import { useQueryLocationFeatureFlags } from '../../feature-flags/queries';
import { useTenDLCInfo } from './10dlc';

type Props = { locationId: string };
export const LocationInfo = ({ locationId }: Props) => {
  const { isLoadingLocation, isLoadingChildren, currentLocation, currentLocationChildren } =
    useLocationServiceContext();

  const { data: phoneTenant } = useTenantDetailsQuery(currentLocation?.phoneTenantId || '', {
    enabled: !!currentLocation?.phoneTenantId,
  });

  const { businessRegistrationStatus, phoneRegistrationStatus, registrationNeedsAttention } = useTenDLCInfo(
    locationId ?? ''
  );

  const { data: featureFlags } = useQueryLocationFeatureFlags(locationId ?? '', {
    enabled: !!locationId,
  });

  const isActive = currentLocation?.active;
  const hasDepartments = featureFlags?.some((flag) => flag.value === true && flag?.flag?.name === 'departments');
  const hasSoftphones = featureFlags?.some(
    (flag) => flag.value === true && flag?.flag?.name === 'toggle-user-softphones'
  );

  if (!locationId) {
    return <>No location selected</>;
  }

  if (isLoadingLocation || isLoadingChildren) {
    return <>Loading...</>;
  }

  const isParent = currentLocationChildren?.length > 0 && !currentLocation?.parentId;
  const isChild = currentLocation?.parentId;
  const isSingleLocation = !isParent && !isChild;

  return (
    <>
      <OutlinedCard
        css={css`
          display: flex;
          justify-content: space-between;
          gap: ${theme.spacing(1)};
          margin-bottom: ${theme.spacing(3)};
        `}
      >
        <div>
          <Heading level={3}>{currentLocation?.name}</Heading>
          <Text color='subdued'>
            {isParent ? 'Parent location' : isChild ? 'Child location' : isSingleLocation && 'Single location'}
          </Text>
        </div>
        <div
          css={css`
            text-align: right;
          `}
        >
          <div
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
          >
            {currentLocation?.active ? (
              <Dot color='success' label={'Location Active'} placement='right' />
            ) : (
              <Dot color='neutral' label='Location Inactive' placement='right' />
            )}
          </div>
          <LocationActions />
        </div>
      </OutlinedCard>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: ${theme.spacing(2)};
        `}
      >
        <SimpleInfoOutlinedCard header='Slug' body={currentLocation?.slug ?? 'Unknown'} canCopyToClipboard={true} />
        <SimpleInfoOutlinedCard
          header='Location Id'
          body={currentLocation?.locationId ?? 'Unknown'}
          canCopyToClipboard={true}
        />
        <SimpleInfoOutlinedCard
          header='Salesforce Id'
          body={currentLocation?.salesforceId || '--'}
          canCopyToClipboard={true}
        />
        <SimpleInfoOutlinedCard
          header='Status'
          body={isActive ? 'Active' : 'Inactive'}
          Icon={<Dot color={isActive ? 'success' : 'neutral'} />}
          canCopyToClipboard={false}
        />
        <SimpleInfoOutlinedCard
          header='Departments'
          body={hasDepartments ? 'Enabled' : 'Disabled'}
          Icon={<Dot color={hasDepartments ? 'success' : 'neutral'} />}
          canCopyToClipboard={false}
        />
        <SimpleInfoOutlinedCard
          header='Softphones'
          body={hasDepartments ? 'Enabled' : 'Disabled'}
          Icon={<Dot color={hasSoftphones ? 'success' : 'neutral'} />}
          canCopyToClipboard={false}
        />
        <SimpleInfoOutlinedCard
          header='Phone Tenant Id'
          body={currentLocation?.phoneTenantId || '--'}
          canCopyToClipboard={true}
        />
        <SimpleInfoOutlinedCard
          header='Data Center'
          body={phoneTenant?.dataCenter ?? '--'}
          canCopyToClipboard={!!phoneTenant?.dataCenter}
        />
        <SimpleInfoOutlinedCard
          header='Tenant Name'
          body={phoneTenant?.name || '--'}
          canCopyToClipboard={!!phoneTenant?.name}
        />
        <SimpleInfoOutlinedCard
          header='Caller Name'
          body={phoneTenant?.callerName || '--'}
          canCopyToClipboard={!!phoneTenant?.callerName}
        />
        <SimpleInfoOutlinedCard
          header='Caller Number'
          body={phoneTenant?.callerNumber?.rawInput || '--'}
          canCopyToClipboard={!!phoneTenant?.callerNumber.rawInput}
        />
        <SimpleInfoOutlinedCard
          header='SMS Number ID'
          body={phoneTenant?.outboundSmsNumberId || '--'}
          canCopyToClipboard={!!phoneTenant?.outboundSmsNumberId}
        />
        <SimpleInfoOutlinedCard
          header='Sms Provider ID'
          body={phoneTenant?.outboundSmsProviderId || '--'}
          canCopyToClipboard={!!phoneTenant?.outboundSmsProviderId}
        />
        <SimpleInfoOutlinedCard
          header='10DLC Biz Status'
          body={businessRegistrationStatus}
          canCopyToClipboard={false}
          Icon={<Dot color={!registrationNeedsAttention ? 'success' : 'neutral'} />}
        />
        <SimpleInfoOutlinedCard
          header='10DLC Phone Status'
          body={phoneRegistrationStatus}
          canCopyToClipboard={false}
          Icon={<Dot color={!registrationNeedsAttention ? 'success' : 'neutral'} />}
        />
      </div>
    </>
  );
};

const LocationActions = () => {
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({ placement: 'top-start' });

  const removeEmailFromDenyList = () => {
    alert('Resetting email');
  };

  const resetPhone = () => {
    alert('Resetting phone');
  };

  return (
    <>
      <Button variant='secondary' {...getTriggerProps()} label='Actions'>
        More Actions
      </Button>
      <PopoverMenu {...getMenuProps()}>
        <PopoverMenuItem {...getItemProps({ index: 0, onClick: () => removeEmailFromDenyList() })}>
          Remove Email From Deny List
        </PopoverMenuItem>
        <PopoverMenuItem {...getItemProps({ index: 1, onClick: () => resetPhone() })}>
          Reset Phone Number Validity
        </PopoverMenuItem>
      </PopoverMenu>
    </>
  );
};
